const CUES = Object.freeze({
  click: [[420, 0.04, "sine"]],
  footstep: [[88, 0.035, "triangle"]],
  attack: [[150, 0.08, "sawtooth"]],
  cast: [
    [440, 0.15, "sine"],
    [660, 0.12, "triangle"],
  ],
  impact: [[72, 0.11, "square"]],
  dodge: [[260, 0.09, "sawtooth"]],
  block: [[110, 0.13, "square"]],
  parry: [[880, 0.12, "triangle"]],
  pickup: [
    [520, 0.08, "sine"],
    [790, 0.12, "triangle"],
  ],
  inventory: [[350, 0.06, "triangle"]],
  crafting: [
    [220, 0.1, "square"],
    [620, 0.15, "sine"],
  ],
  vendor: [[330, 0.09, "sine"]],
  quest: [
    [392, 0.12, "sine"],
    [587, 0.18, "triangle"],
  ],
  level: [
    [330, 0.12, "sine"],
    [494, 0.12, "sine"],
    [740, 0.2, "sine"],
  ],
  checkpoint: [
    [294, 0.14, "sine"],
    [440, 0.2, "triangle"],
  ],
  death: [
    [130, 0.24, "sawtooth"],
    [65, 0.42, "sine"],
  ],
  phase: [
    [82, 0.3, "square"],
    [165, 0.25, "triangle"],
  ],
  victory: [
    [261, 0.14, "sine"],
    [392, 0.14, "sine"],
    [523, 0.28, "sine"],
  ],
});

const MUSIC = Object.freeze({
  menu: [110, 146.83, 164.81, 220],
  hub: [130.81, 164.81, 196, 261.63],
  grass: [98, 146.83, 196, 220],
  ash: [82.41, 110, 138.59, 164.81],
  marsh: [110, 138.59, 185, 207.65],
  ice: [146.83, 220, 293.66, 329.63],
  hollow: [73.42, 110, 123.47, 164.81],
  boss: [65.41, 98, 130.81, 196],
});

export class AudioManager {
  constructor(settings) {
    this.settings = settings;
    this.context = null;
    this.master = null;
    this.musicGain = null;
    this.effectsGain = null;
    this.musicMode = "menu";
    this.musicAccumulator = 0;
    this.musicIndex = 0;
    this.activeNodes = new Set();
    this.maxActiveNodes = 48;
    this.paused = true;
    this.hidden = document.hidden;
    this.visibilityHandler = () => {
      this.hidden = document.hidden;
      this.sync();
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }
  ensureContext() {
    if (this.context) return this.context;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    try {
      this.context = new AudioContextClass();
      this.master = this.context.createGain();
      this.musicGain = this.context.createGain();
      this.effectsGain = this.context.createGain();
      this.musicGain.connect(this.master);
      this.effectsGain.connect(this.master);
      this.master.connect(this.context.destination);
      this.applySettings();
      return this.context;
    } catch (_error) {
      this.context = null;
      return null;
    }
  }
  unlock() {
    const context = this.ensureContext();
    if (context?.state === "suspended" && !this.hidden)
      context.resume().catch(() => undefined);
  }
  applySettings(settings = this.settings) {
    this.settings = settings;
    if (!this.context) return;
    this.master.gain.setTargetAtTime(
      settings.masterVolume,
      this.context.currentTime,
      0.03,
    );
    this.musicGain.gain.setTargetAtTime(
      settings.musicVolume,
      this.context.currentTime,
      0.03,
    );
    this.effectsGain.gain.setTargetAtTime(
      settings.effectsVolume,
      this.context.currentTime,
      0.03,
    );
  }
  tone(frequency, duration, type, gain, delay = 0) {
    if (!this.context || !gain || this.activeNodes.size >= this.maxActiveNodes)
      return;
    try {
      const start = this.context.currentTime + delay;
      const oscillator = this.context.createOscillator();
      const envelope = this.context.createGain();
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(Math.max(30, frequency), start);
      oscillator.frequency.exponentialRampToValueAtTime(
        Math.max(30, frequency * 0.94),
        start + duration,
      );
      envelope.gain.setValueAtTime(0.0001, start);
      envelope.gain.exponentialRampToValueAtTime(0.035, start + 0.008);
      envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      oscillator.connect(envelope);
      envelope.connect(gain);
      const record = { oscillator, envelope };
      this.activeNodes.add(record);
      oscillator.addEventListener(
        "ended",
        () => {
          oscillator.disconnect();
          envelope.disconnect();
          this.activeNodes.delete(record);
        },
        { once: true },
      );
      oscillator.start(start);
      oscillator.stop(start + duration + 0.03);
    } catch (_error) {
      /* Audio is always non-fatal. */
    }
  }
  play(name) {
    if (!CUES[name] || this.hidden || !this.settings.effectsVolume) return;
    this.unlock();
    CUES[name].forEach(([frequency, duration, type], index) =>
      this.tone(frequency, duration, type, this.effectsGain, index * 0.055),
    );
  }
  setMusic(mode) {
    const next = MUSIC[mode] ? mode : "menu";
    if (next === this.musicMode) return;
    this.musicMode = next;
    this.musicIndex = 0;
  }
  setPaused(paused) {
    this.paused = Boolean(paused);
    this.sync();
  }
  sync() {
    if (!this.context || !this.musicGain) return;
    const volume =
      this.paused || this.hidden ? 0.0001 : this.settings.musicVolume;
    this.musicGain.gain.setTargetAtTime(volume, this.context.currentTime, 0.08);
  }
  update(dt) {
    if (this.paused || this.hidden || !this.settings.musicVolume) return;
    this.unlock();
    this.musicAccumulator += Math.min(0.2, dt);
    if (this.musicAccumulator < 0.32) return;
    this.musicAccumulator %= 0.32;
    const notes = MUSIC[this.musicMode];
    const note = notes[this.musicIndex % notes.length];
    const accent = this.musicIndex % 8 === 6 ? 2 : 1;
    this.musicIndex = (this.musicIndex + 1) % 64;
    this.tone(
      note * accent,
      0.26,
      this.musicMode === "boss" ? "sawtooth" : "triangle",
      this.musicGain,
    );
  }
  teardown() {
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    if (this.context) {
      for (const { oscillator, envelope } of this.activeNodes) {
        try {
          oscillator.stop();
          oscillator.disconnect();
          envelope.disconnect();
        } catch (_error) {
          /* Already-stopped nodes are safe to ignore. */
        }
      }
      this.activeNodes.clear();
      this.musicGain?.disconnect();
      this.effectsGain?.disconnect();
      this.master?.disconnect();
      this.context.close().catch(() => undefined);
      this.context = null;
    }
    this.master = null;
    this.musicGain = null;
    this.effectsGain = null;
  }
}
